import { useState } from "react";
import { Link } from "react-router-dom";

const steps = [
  {
    num: "01",
    icon: "ri-user-add-line",
    title: "会員登録・事業者モードへ切替",
    desc: "まず無料で会員登録をしてください。登録後、ヘッダーの「事業者モード」へ切り替えることで出品者としての操作が可能になります。",
    tips: [
      "メールアドレスとパスワードだけで登録できます",
      "既に会員の方はログイン後にモード切替するだけでOK",
      "登録後すぐにフリープランで出品開始できます",
    ],
  },
  {
    num: "02",
    icon: "ri-store-2-line",
    title: "事業者プロフィールを設定する",
    desc: "購入者に信頼してもらうために、事業者名・理念・経歴・得意分野などのプロフィールを丁寧に記入しましょう。顔となる大切なページです。",
    tips: [
      "サービス名・キャッチコピー・理念文は必ず記載する",
      "スキルタグを設定すると検索に引っかかりやすくなる",
      "プロフィール画像・カバー画像は印象を大きく左右します",
    ],
  },
  {
    num: "03",
    icon: "ri-video-add-line",
    title: "動画講座または教材キットを出品する",
    desc: "出品は「動画講座」と「教材キット」の2種類。推奨価格を参考に設定し、無料サンプルを添えることで購入率が大幅に上がります。",
    tips: [
      "無料サンプル（3〜8分）は必ず設定しましょう",
      "動画時間に応じた推奨価格が自動表示されます",
      "教材キットは仕入れ価格を入力するだけで推奨価格を自動計算",
    ],
  },
  {
    num: "04",
    icon: "ri-eye-line",
    title: "無料サンプルで購入者を引き付ける",
    desc: "3〜8分の無料サンプルを公開することで、購入前に技術の深さを感じてもらえます。最初の購入への最大の導線です。",
    tips: [
      "サンプルの内容は「なぜこの技術が必要か」を伝えると効果的",
      "自己紹介を冒頭に入れると親近感が生まれます",
      "動画品質より内容の誠実さが信頼につながります",
    ],
  },
  {
    num: "05",
    icon: "ri-message-3-line",
    title: "購入者とメッセージでつながる",
    desc: "購入済みの受講者からのメッセージに丁寧に答えることで、リピートやレビューにつながります。応答率も信頼の指標になります。",
    tips: [
      "質問には48時間以内の返信を心がけましょう",
      "メッセージ機能は購入済みユーザー限定です",
      "丁寧な対応がレビューに直結します",
    ],
  },
  {
    num: "06",
    icon: "ri-bar-chart-line",
    title: "データで改善・ライトプランへ",
    desc: "ライトプランではアクセス・購入・フォロー数などのデータが閲覧でき、どの講座が人気かを把握して改善できます。売上が安定してきたら検討を。",
    tips: [
      "フリープランでも十分な売上が出てからアップグレードを",
      "ライトプランは手数料が半分になるため高売上ほど効果大",
      "掲載申請でトップページへの露出機会も増えます",
    ],
  },
];

const faqs = [
  { q: "どんな業種でも出品できますか？", a: "はい。ニッチなサービス業・職人技術であれば業種を問わず出品できます。しみ抜き・和服ケア・大工技術・和菓子・陶芸・理容・書道など幅広いジャンルに対応しています。" },
  { q: "動画はどのように撮影すればよいですか？", a: "スマートフォンでの撮影で十分です。重要なのは映像の美しさより「技術の伝わりやすさ」です。手元のアップと全体のバランス、声の聞きやすさを意識してください。" },
  { q: "フリープランで十分ですか？", a: "まずはフリープランで出品を始め、売上が安定してきたらライトプランへの移行を検討してください。フリープランでも動画3本・教材3点まで無料で出品できます。" },
  { q: "審査はありますか？", a: "出品内容について管理者による確認を行っています。サービス業・職人技術の分野であれば通常問題ありません。著作権侵害・虚偽内容は掲載できません。" },
  { q: "販売手数料はいつ発生しますか？", a: "商品が売れた時点で手数料が発生します。売れなければ費用はかかりません（ライトプランの月額除く）。" },
  { q: "価格はどう設定すればよいですか？", a: "出品画面に推奨価格が自動表示されます。動画は動画時間に応じて、教材キットは仕入れ価格に応じた推奨価格が確認できます。" },
];

export default function GuidePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-deep-green overflow-hidden py-24 px-4">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/fbb5ea340201fb9c888c98bd08f7e42a.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-screen-lg mx-auto text-center">
          <p className="text-gold text-xs font-display italic tracking-widest mb-4">Seller Guide</p>
          <h1 className="font-serif text-white text-4xl md:text-5xl font-light leading-relaxed mb-6">
            出品ガイド
          </h1>
          <p className="text-white/70 font-sans text-base leading-relaxed max-w-xl mx-auto mb-8">
            TSUGITEで技術を伝えるために必要な手順を、ステップごとに解説します。<br className="hidden md:block" />
            はじめての方も、この流れに沿って進めれば安心してスタートできます。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="bg-gold text-white px-8 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-amber-500 transition-colors whitespace-nowrap inline-block">
              無料で出品を始める →
            </Link>
            <Link to="/pricing" className="border border-white/40 text-white px-8 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap inline-block">
              料金プランを確認する
            </Link>
          </div>
        </div>
      </section>

      {/* Overview Stats */}
      <section className="bg-ivory py-12 px-4 border-b border-stone-100">
        <div className="max-w-screen-lg mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: "ri-time-line", value: "最短30分", label: "出品準備時間" },
              { icon: "ri-money-cny-circle-line", value: "0円", label: "初期費用・月額" },
              { icon: "ri-percent-line", value: "20%", label: "フリープラン販売手数料" },
              { icon: "ri-shield-check-line", value: "安心", label: "管理者サポート付き" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-2xl p-5 text-center border border-stone-100">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-deep-green/8 mx-auto mb-3">
                  <i className={`${s.icon} text-xl text-teal`} />
                </div>
                <p className="font-serif text-deep-green text-xl mb-1">{s.value}</p>
                <p className="text-xs text-charcoal/50 font-sans">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-screen-lg mx-auto">
          <div className="mb-12 text-center">
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Step by Step</p>
            <h2 className="font-serif text-deep-green text-3xl font-light">出品の流れ</h2>
          </div>
          <div className="space-y-8">
            {steps.map((step, i) => (
              <div key={step.num} className={`rounded-3xl p-8 border ${i % 2 === 0 ? "bg-ivory border-stone-100" : "bg-white border-stone-100"}`}>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex items-start gap-5 flex-1">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-deep-green text-gold">
                        <i className={`${step.icon} text-2xl`} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-gold/50 font-display text-3xl font-light leading-none">{step.num}</span>
                        <h3 className="font-serif text-deep-green text-lg">{step.title}</h3>
                      </div>
                      <p className="text-charcoal/70 font-sans text-sm leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                  <div className="md:w-72 flex-shrink-0">
                    <div className="bg-white rounded-xl p-4 border border-stone-100">
                      <p className="text-xs text-teal font-sans font-medium mb-3 flex items-center gap-1.5">
                        <i className="ri-lightbulb-line" />ポイント
                      </p>
                      <ul className="space-y-2">
                        {step.tips.map((t) => (
                          <li key={t} className="flex items-start gap-2 text-xs text-charcoal/60 font-sans">
                            <i className="ri-check-line text-teal flex-shrink-0 mt-0.5" />
                            {t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Price Guide */}
      <section className="py-20 px-4 bg-ivory">
        <div className="max-w-screen-lg mx-auto">
          <div className="mb-12 text-center">
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">Pricing Reference</p>
            <h2 className="font-serif text-deep-green text-3xl font-light">価格設定の目安</h2>
            <p className="text-charcoal/60 font-sans text-sm mt-3">出品画面でも自動表示されますが、事前に確認しておきましょう。</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Video pricing */}
            <div className="bg-white rounded-3xl p-7 border border-stone-100">
              <h3 className="font-serif text-deep-green text-lg mb-5 flex items-center gap-2">
                <i className="ri-play-circle-line text-teal" />動画講座の推奨価格
              </h3>
              <div className="space-y-2">
                {[
                  { range: "3〜8分", price: "0円", label: "無料サンプル", color: "bg-stone-50" },
                  { range: "10〜20分", price: "1,480円", label: "お試し講座", color: "bg-teal/5" },
                  { range: "21〜35分", price: "2,980円", label: "入門講座", color: "bg-teal/5" },
                  { range: "36〜50分", price: "4,980円", label: "基礎講座", color: "bg-gold/5" },
                  { range: "51〜70分", price: "6,980円", label: "実践講座", color: "bg-gold/5" },
                  { range: "71〜90分", price: "8,980円", label: "中上級講座", color: "bg-gold/8" },
                  { range: "91〜120分", price: "11,800円", label: "本格講座", color: "bg-deep-green/5" },
                  { range: "121〜180分", price: "16,800円", label: "専門講座", color: "bg-deep-green/5" },
                ].map((row) => (
                  <div key={row.range} className={`flex items-center justify-between px-4 py-2.5 rounded-xl ${row.color}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-charcoal/50 font-sans w-20 flex-shrink-0">{row.range}</span>
                      <span className="text-xs text-charcoal/60 font-sans">{row.label}</span>
                    </div>
                    <span className="font-serif text-deep-green text-sm font-medium">{row.price}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Kit pricing */}
            <div className="bg-white rounded-3xl p-7 border border-stone-100">
              <h3 className="font-serif text-deep-green text-lg mb-5 flex items-center gap-2">
                <i className="ri-box-3-line text-teal" />教材キットの推奨価格倍率
              </h3>
              <p className="text-xs text-charcoal/60 font-sans mb-4 leading-relaxed">
                Amazon等での仕入れ価格（購入単価）に対して、以下の倍率が推奨販売価格の目安です。
              </p>
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-sans font-medium text-charcoal mb-2 flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center rounded-full bg-teal/10 text-teal text-xs"><i className="ri-leaf-line" /></span>
                    フリープラン
                  </p>
                  <div className="space-y-1.5">
                    {[
                      { range: "〜3,000円", rate: "×1.60（160%）" },
                      { range: "3,001〜8,000円", rate: "×1.55（155%）" },
                      { range: "8,001〜15,000円", rate: "×1.50（150%）" },
                      { range: "15,001円〜", rate: "×1.45（145%）" },
                    ].map((r) => (
                      <div key={r.range} className="flex justify-between items-center bg-stone-50 rounded-xl px-4 py-2">
                        <span className="text-xs text-charcoal/60 font-sans">{r.range}</span>
                        <span className="text-xs font-sans text-teal font-medium">{r.rate}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-sans font-medium text-charcoal mb-2 flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center rounded-full bg-gold/10 text-gold text-xs"><i className="ri-star-line" /></span>
                    ライトプラン
                  </p>
                  <div className="space-y-1.5">
                    {[
                      { range: "〜3,000円", rate: "×1.50（150%）" },
                      { range: "3,001〜8,000円", rate: "×1.45（145%）" },
                      { range: "8,001〜15,000円", rate: "×1.40（140%）" },
                      { range: "15,001円〜", rate: "×1.35（135%）" },
                    ].map((r) => (
                      <div key={r.range} className="flex justify-between items-center bg-gold/5 rounded-xl px-4 py-2">
                        <span className="text-xs text-charcoal/60 font-sans">{r.range}</span>
                        <span className="text-xs font-sans text-gold font-medium">{r.rate}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 p-3 bg-ivory rounded-xl">
                <p className="text-xs text-charcoal/60 font-sans leading-relaxed">
                  <strong className="text-charcoal">計算例：</strong>仕入れ5,000円の教材キットを<br />
                  フリープランで販売 → 推奨 <strong className="text-teal">7,750円</strong><br />
                  ライトプランで販売 → 推奨 <strong className="text-gold">7,250円</strong>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-screen-md mx-auto">
          <div className="mb-10 text-center">
            <p className="text-gold text-xs font-display italic tracking-widest mb-2">FAQ</p>
            <h2 className="font-serif text-deep-green text-3xl font-light">よくある質問</h2>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-100 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-stone-50 transition-colors"
                >
                  <span className="font-sans text-sm text-charcoal font-medium pr-4">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-xl text-charcoal/30 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <p className="text-sm text-charcoal/70 font-sans leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-charcoal/50 text-sm font-sans mb-3">解決しない場合は</p>
            <Link to="/contact" className="text-teal font-sans text-sm hover:underline cursor-pointer">お問い合わせフォームへ →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-ivory">
        <div className="max-w-screen-sm mx-auto text-center">
          <p className="text-gold text-xs font-display italic tracking-widest mb-3">Ready to Start?</p>
          <h2 className="font-serif text-deep-green text-2xl font-light mb-4">
            あなたの技術を、今日から届けてください。
          </h2>
          <p className="text-charcoal/60 text-sm font-sans leading-relaxed mb-7">
            初期費用・月額料金は一切かかりません。<br />売れた時だけ手数料が発生します。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" className="bg-deep-green text-white px-8 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap inline-block">
              無料で登録する →
            </Link>
            <Link to="/pricing" className="border border-deep-green/30 text-deep-green px-8 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-ivory transition-colors whitespace-nowrap inline-block">
              プラン詳細を見る
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
